import {ChangeDetectionStrategy, Component, inject, Input, OnInit} from '@angular/core';
import {AbstractControl, FormsModule, ReactiveFormsModule, UntypedFormControl, UntypedFormGroup, Validators} from '@angular/forms';
import {NgMultiLabelTemplateDirective, NgSelectComponent} from '@ng-select/ng-select';
import {BaseComponent, FeatureTypes, FeatureValue, MultipleSelectValidatorContent, TemplateFeature} from 'compob2b-libs';
import {Observable} from 'rxjs';
import {distinctUntilChanged, map, takeUntil, tap} from 'rxjs/operators';
import {
  TemplateFeatureService,
  TemplateFeatureValue
} from '../../../../../../compob2b-admin/src/app/cabinet/pim/product/create-update/features/template-feature.service'
import {AsyncPipe, NgFor, NgIf, SlicePipe} from '@angular/common';
import {NbIconModule, NbTooltipModule} from '@nebular/theme';
import {RouterLink} from '@angular/router';

@Component({
    selector: 'app-tf-select',
    templateUrl: './tf-select.component.html',
    changeDetection: ChangeDetectionStrategy.OnPush,
    imports: [NgIf, NbTooltipModule, NbIconModule, FormsModule, ReactiveFormsModule, NgSelectComponent, NgMultiLabelTemplateDirective, NgFor, RouterLink, AsyncPipe, SlicePipe]
})
export class TfSelectComponent extends BaseComponent implements OnInit {
  @Input() feature: TemplateFeature;
  @Input() featureLink = false;

  loading = true;

  form = new UntypedFormGroup({
    featureValue: new UntypedFormControl(null)
  });

  public featureValues$: Observable<FeatureValue[]>;
  private featureValuesCount = 0;

  showItemsCount = this.environment.visibleSelectTagCount;

  isValidatorSet = false;
  validatorName: string = null;
  multipleSelectValidator: MultipleSelectValidatorContent = null;

  templateFeatureService = inject(TemplateFeatureService);

  ngOnInit(): void {
    this.setValidator();

    const uri = `/feature-value/feature/${this.feature.id}/template/${this.feature.templateId}`;
    this.featureValues$ = this.compoApiService.get<FeatureValue[]>(uri)
      .pipe(
        takeUntil(this.unsubscribe),
        map(response => {
          return this.compoApiService.naturalSort(response.data, 'value');
        }),
        tap(values => {
          this.loading = false;
          this.featureValuesCount = values.length;
        }),
      );

    this.form.get('featureValue').valueChanges
      .pipe(
        distinctUntilChanged(),
        takeUntil(this.unsubscribe),
      )
      .subscribe(data => {
        this.setFeature(data, this.featureValue.valid, this.isShowEmptyWarning());
        this.cdr.detectChanges();
      });

    this.setDefaultValue();

    this.templateFeatureService.isFeaturesModelReloaded()
      .pipe(takeUntil(this.unsubscribe))
      .subscribe(_ => {
        this.setDefaultValue();
        this.cdr.detectChanges();
      });
  }

  private setValidator(): void {
    if (this.feature.validator !== null && this.isMultiple()) {
      this.isValidatorSet = true;
      this.validatorName = this.feature.validator.header;
      this.multipleSelectValidator = this.feature.validator.content.multipleSelectValidator;

      const validators = [];
      if (this.multipleSelectValidator.minValuesNumber !== null) {
        validators.push(Validators.minLength(this.multipleSelectValidator.minValuesNumber));
      }

      if (this.multipleSelectValidator.maxValuesNumber !== null) {
        validators.push(Validators.maxLength(this.multipleSelectValidator.maxValuesNumber));
      }

      if (validators.length > 0) {
        this.featureValue.addValidators(validators);
      }
    }
  }

  private setDefaultValue(): void {
    const featureModel = this.templateFeatureService.getFeatureModel();

    if (this.feature.id in featureModel) {
      this.featureValue.setValue(featureModel[this.feature.id], {emitEvent: false});
    }

    this.setDefaultFeatureValue(this.featureValue.value);
  }

  private setFeature(featureValue: TemplateFeatureValue, valid: boolean, empty: boolean): void {
    this.templateFeatureService.setFeature(
      this.feature.groupId,
      this.feature.id,
      featureValue,
      valid,
      empty
    );
  }

  private setDefaultFeatureValue(featureValue: TemplateFeatureValue): void {
    this.templateFeatureService.setDefaultFeatureValue(
      this.feature.groupId,
      this.feature.id,
      featureValue,
      this.form.valid,
      this.isShowEmptyWarning()
    );
  }

  get featureValue(): AbstractControl {
    return this.form.get('featureValue');
  }

  isEmpty(): boolean {
    return this.featureValue.value === null || this.featureValue.value.length === 0;
  }

  isRequired(): boolean {
    return this.feature.required;
  }

  isShowEmptyWarning(): boolean {
    return this.isRequired() && this.isEmpty();
  }

  isMultiple(): boolean {
    return FeatureTypes.CHECKBOX === this.feature.featureTypeId
      || (FeatureTypes.SELECT === this.feature.featureTypeId && this.feature.multiple === true);
  }

  showAllSelectedValues(select: NgSelectComponent) {
    this.showItemsCount = this.featureValuesCount;
    select.focus();
  }

  hideExtraSelectedValues() {
    this.showItemsCount = this.environment.visibleSelectTagCount;
  }

  showHideButton(selectedValueCount: number): boolean {
    return this.isMultiple()
      && this.featureValuesCount === this.showItemsCount && selectedValueCount > this.environment.visibleSelectTagCount;
  }

  showAllSelectedValuesButton(selectedValueCount: number): boolean {
    return this.isMultiple() && selectedValueCount > this.showItemsCount;
  }

  getHiddenSelectedItemsCount(selectedValueCount: number): number {
    return selectedValueCount - this.environment.visibleSelectTagCount;
  }

  onBlur($event: any) {
    this.hideExtraSelectedValues();
  }
}
